/**
 * @module features/files/file-search
 * @description Client-side file search and filtering functionality
 */

import { logger } from '../../utils/logger.js';

// Module state
let allFiles = [];
let filteredFiles = [];
let currentSearchTerm = '';

/**
 * Initialize file search functionality
 */
export function initializeFileSearch() {
    const searchInput = document.getElementById('file-search-input');
    const clearButton = document.getElementById('clear-file-search-btn');

    if (!searchInput) {
        logger.warn('File search input not found');
        return;
    }

    // Real-time search (no debounce needed for client-side filtering)
    searchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value.trim();
        performFileSearch(searchTerm);
    });

    // Clear button
    if (clearButton) {
        clearButton.addEventListener('click', () => {
            searchInput.value = '';
            clearFileSearch();
        });
    }

    logger.debug('File search initialized');
}

/**
 * Set the files to be searched
 * @param {Array} files - Array of file objects
 */
export function setSearchableFiles(files) {
    allFiles = files || [];
    filteredFiles = [...allFiles];
    currentSearchTerm = '';

    // Show search container if there are files
    const searchContainer = document.getElementById('file-search-container');
    if (searchContainer) {
        if (allFiles.length > 0) {
            searchContainer.classList.remove('hidden');
        } else {
            searchContainer.classList.add('hidden');
        }
    }

    // Reset search input
    const searchInput = document.getElementById('file-search-input');
    if (searchInput) {
        searchInput.value = '';
    }

    // Hide clear button and count
    updateSearchUI(false, 0, 0);
}

/**
 * Perform file search/filtering
 * @param {string} searchTerm - Search term to filter by
 */
function performFileSearch(searchTerm) {
    currentSearchTerm = searchTerm.toLowerCase();

    if (!currentSearchTerm) {
        clearFileSearch();
        return;
    }

    // Filter files by name, category, or content type
    filteredFiles = allFiles.filter(file => {
        const fileName = (file.name || file.fileName || '').toLowerCase();
        const category = (file.category || '').toLowerCase();
        const contentType = (file.contentType || '').toLowerCase();
        const fileType = getFileTypeDescription(contentType).toLowerCase();

        return fileName.includes(currentSearchTerm) ||
               category.includes(currentSearchTerm) ||
               contentType.includes(currentSearchTerm) ||
               fileType.includes(currentSearchTerm);
    });

    // Update UI
    updateSearchUI(true, filteredFiles.length, allFiles.length);

    // Filter visible files in the DOM
    filterFileElements();

    logger.debug(`File search: "${searchTerm}" - ${filteredFiles.length} of ${allFiles.length} files match`);
}

/**
 * Clear file search and show all files
 */
function clearFileSearch() {
    currentSearchTerm = '';
    filteredFiles = [...allFiles];

    // Update UI
    updateSearchUI(false, 0, 0);

    // Show all files
    showAllFileElements();

    logger.debug('File search cleared');
}

/**
 * Update search UI elements
 * @param {boolean} showClear - Whether to show clear button
 * @param {number} matchCount - Number of matching files
 * @param {number} totalCount - Total number of files
 */
function updateSearchUI(showClear, matchCount, totalCount) {
    const clearButton = document.getElementById('clear-file-search-btn');
    const countContainer = document.getElementById('file-search-count');
    const countText = document.getElementById('file-search-count-text');

    // Update clear button visibility
    if (clearButton) {
        if (showClear) {
            clearButton.classList.remove('hidden');
        } else {
            clearButton.classList.add('hidden');
        }
    }

    // Update count display
    if (countContainer && countText) {
        if (showClear) {
            countContainer.classList.remove('hidden');

            if (matchCount === 0) {
                countText.textContent = `No files match your search`;
                countText.className = 'no-results';
            } else if (matchCount === totalCount) {
                countText.textContent = `Showing all ${totalCount} files`;
                countText.className = 'all-results';
            } else {
                countText.textContent = `Showing ${matchCount} of ${totalCount} files`;
                countText.className = 'filtered-results';
            }
        } else {
            countContainer.classList.add('hidden');
        }
    }
}

/**
 * Filter file elements in the DOM
 */
function filterFileElements() {
    const filesContainer = document.getElementById('files-container');
    if (!filesContainer) return;

    const fileElements = filesContainer.querySelectorAll('.file-item');
    const matchingFileIds = new Set(filteredFiles.map(f => f.archivedFileObjectId));

    fileElements.forEach(element => {
        const fileId = element.getAttribute('data-file-id');
        if (matchingFileIds.has(fileId)) {
            element.style.display = '';
        } else {
            element.style.display = 'none';
        }
    });

    // Show "no results" message if no files match
    if (filteredFiles.length === 0 && currentSearchTerm) {
        const noResultsMsg = filesContainer.querySelector('.no-search-results');
        if (!noResultsMsg) {
            const msg = document.createElement('p');
            msg.className = 'no-search-results';
            msg.textContent = 'No files match your search criteria.';
            filesContainer.insertBefore(msg, filesContainer.firstChild);
        }
    } else {
        // Remove "no results" message if it exists
        const noResultsMsg = filesContainer.querySelector('.no-search-results');
        if (noResultsMsg) {
            noResultsMsg.remove();
        }
    }
}

/**
 * Show all file elements in the DOM
 */
function showAllFileElements() {
    const filesContainer = document.getElementById('files-container');
    if (!filesContainer) return;

    const fileElements = filesContainer.querySelectorAll('.file-item');
    fileElements.forEach(element => {
        element.style.display = '';
    });

    // Remove "no results" message
    const noResultsMsg = filesContainer.querySelector('.no-search-results');
    if (noResultsMsg) {
        noResultsMsg.remove();
    }
}

/**
 * Get user-friendly file type description
 * @param {string} contentType - MIME type
 * @returns {string} User-friendly description
 */
function getFileTypeDescription(contentType) {
    if (!contentType) return 'Unknown';

    // Common mappings
    const typeMap = {
        'image/jpeg': 'JPEG Image',
        'image/jpg': 'JPG Image',
        'image/png': 'PNG Image',
        'image/gif': 'GIF Image',
        'image/svg+xml': 'SVG Image',
        'image/webp': 'WebP Image',
        'application/pdf': 'PDF Document',
        'application/msword': 'Word Document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
        'application/vnd.ms-excel': 'Excel Spreadsheet',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
        'application/vnd.ms-powerpoint': 'PowerPoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
        'text/plain': 'Text File',
        'text/csv': 'CSV File',
        'application/zip': 'ZIP Archive',
        'application/x-rar-compressed': 'RAR Archive',
        'application/x-7z-compressed': '7Z Archive'
    };

    if (typeMap[contentType]) {
        return typeMap[contentType];
    }

    // Generic type extraction
    if (contentType.startsWith('image/')) return 'Image';
    if (contentType.startsWith('video/')) return 'Video';
    if (contentType.startsWith('audio/')) return 'Audio';
    if (contentType.startsWith('text/')) return 'Text';

    return contentType;
}

/**
 * Get current search term
 * @returns {string} Current search term
 */
export function getCurrentSearchTerm() {
    return currentSearchTerm;
}

/**
 * Get filtered files
 * @returns {Array} Array of filtered file objects
 */
export function getFilteredFiles() {
    return filteredFiles;
}

/**
 * Get all files
 * @returns {Array} Array of all file objects
 */
export function getAllFiles() {
    return allFiles;
}

export default {
    initializeFileSearch,
    setSearchableFiles,
    getCurrentSearchTerm,
    getFilteredFiles,
    getAllFiles
};
